// ScheduleModal — the create + edit surface for a calendar tile.
//
// Create mode: pre-filled from a day click on MonthGrid. User picks a
// post (grid of approved My Creatives thumbnails), date, hour, and 1+
// platforms. Submit fans out to N rows (one per platform).
//
// Edit mode: lock the post (changing posts on an existing tile = cancel
// + recreate; cleaner). Allow date/hour/platform changes. Delete button
// in the footer.
//
// Image thumbnails come from the private gallery bucket via
// getSignedImageUrl — resolved once on mount, cached per post id.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  type FacebookConnectionStatus,
  type ScheduledPost,
  createScheduledPosts,
  updateScheduledPost,
  deleteScheduledPost,
  InsufficientCreditsError,
} from '@/lib/api';
import { useConfirm } from '@/hooks/useConfirm';
import { type Post, getSignedImageUrl, getSignedImageUrls } from '@/lib/gallery';
import { PLATFORM_META, PLATFORM_PICK_ORDER } from './platforms';

interface ScheduleModalProps {
  /** When editing, the cohort of tiles that share a single scheduled_at
   *  (all the platforms the user originally fanned out to in one
   *  create-mode submission). Use editCohortTiles() from lib/
   *  scheduledByPost to compute. Empty array OR null = create mode. */
  editingTiles: ScheduledPost[] | null;
  /** Day pre-fill for create mode. Ignored when editing. */
  createDay: Date | null;
  /** Approved posts library — drives the picker grid. */
  posts: Post[];
  /** Meta connection state — drives per-platform availability (Instagram
   *  + Facebook require Meta OAuth; TikTok + X have no integration yet so
   *  they show as "Coming soon"). Pass null when unknown — chips render
   *  as fully enabled (graceful fallback so callsites that haven't
   *  fetched connection state still work). */
  fbStatus: FacebookConnectionStatus | null;
  onClose: () => void;
  onSaved: () => void;
}

/** Per-platform availability — drives the chip's disabled state + tooltip.
 *  Used to be implicit ("all chips clickable, user finds out at publish
 *  time"); now we surface it at schedule time so users only schedule on
 *  what'll actually publish. */
type PlatformAvailability =
  | { state: 'connected' }
  | { state: 'needs_connection'; hint: string }
  | { state: 'coming_soon'; hint: string };

function platformAvailability(
  platformId: string,
  fbStatus: FacebookConnectionStatus | null,
): PlatformAvailability {
  // TikTok + X always return coming_soon — they have no OAuth integration
  // at all, so the gate is independent of FB connection status. (Was bugged
  // June 8: an early `if (fbStatus === null) return connected` ran first,
  // which made TikTok/X look enabled on the /schedule calendar's modal
  // because that callsite passed fbStatus=null. Order matters.)
  if (platformId !== 'facebook' && platformId !== 'instagram') {
    return { state: 'coming_soon', hint: 'Coming soon — OAuth integration not built yet' };
  }
  // Facebook + Instagram require Meta OAuth. If the caller didn't pass
  // fbStatus (legacy callsite — should be rare now that useFacebookConnection
  // is mounted at app root), treat as connected so users aren't locked out.
  if (fbStatus === null) return { state: 'connected' };
  if (platformId === 'facebook') {
    return fbStatus.connected
      ? { state: 'connected' }
      : { state: 'needs_connection', hint: 'Connect Facebook in the sidebar first' };
  }
  // instagram
  return fbStatus.instagramUserId
    ? { state: 'connected' }
    : {
        state: 'needs_connection',
        hint: 'Link an Instagram Business account to your Facebook Page',
      };
}

// Hours offered in the picker (0–23). Display in user-local 12-hour format
// with the underlying 24-hour value as the option's value.
const HOURS_24: number[] = Array.from({ length: 24 }, (_, i) => i);
// Minutes offered in the time picker — 5-minute increments (00, 05, … 55).
const MINUTES: number[] = Array.from({ length: 12 }, (_, i) => i * 5);

export default function ScheduleModal({
  editingTiles,
  createDay,
  posts,
  fbStatus,
  onClose,
  onSaved,
}: ScheduleModalProps) {
  const isEdit = !!editingTiles && editingTiles.length > 0;
  const confirm = useConfirm();

  // ----- Original snapshot (edit mode only) -----
  // Capture the cohort's original timestamp + platform set ONCE so we can
  // compute `hasChanges` reliably even after the user toggles things back
  // to the original. Memoized on the prop so repeat rerenders don't reseed
  // when the parent passes the same array reference.
  const originalSnapshot = useMemo(() => {
    if (!isEdit || !editingTiles) return null;
    const sorted = [...editingTiles].sort((a, b) =>
      a.scheduled_at.localeCompare(b.scheduled_at),
    );
    return {
      scheduledAt: sorted[0].scheduled_at,
      platforms: Array.from(new Set(sorted.map((t) => t.platform))).sort(),
      tilesByPlatform: new Map(editingTiles.map((t) => [t.platform, t])),
    };
  }, [editingTiles, isEdit]);

  // ----- State -----
  // Auto-select when the picker has a single post (e.g. opened from a
  // gallery tile's Schedule icon or the detail page Schedule button —
  // both pass posts={[onePost]}). Otherwise the user has to click the
  // only visible thumbnail before Submit enables.
  //
  // For Ad Pilot auto_generate tiles, the source post lives in
  // `generated_post_id` (worker stamps it after render) — `post_id` stays
  // NULL. Fall through to whichever is set so the LockedPostPreview can
  // resolve the actual rendered ad instead of falsely warning "Source
  // post is missing".
  const [selectedPostId, setSelectedPostId] = useState<string | null>(
    editingTiles?.[0]?.post_id
      ?? editingTiles?.[0]?.generated_post_id
      ?? (posts.length === 1 ? posts[0].id : null),
  );
  const [dateStr, setDateStr] = useState<string>(() => {
    // ISO yyyy-mm-dd in local time, suitable for <input type="date">.
    const seed = originalSnapshot
      ? new Date(originalSnapshot.scheduledAt)
      : (createDay || new Date());
    return toLocalDateInputValue(seed);
  });
  const [hour, setHour] = useState<number>(() => {
    if (originalSnapshot) return new Date(originalSnapshot.scheduledAt).getHours();
    // Default to the next round hour from now — feels less like
    // "this minute" and more like a deliberate slot.
    return Math.min(23, new Date().getHours() + 1);
  });
  const [minute, setMinute] = useState<number>(() => {
    if (originalSnapshot) {
      // Round the saved minute to the nearest 5 so it lands on a picker option.
      const mm = new Date(originalSnapshot.scheduledAt).getMinutes();
      return Math.min(55, Math.round(mm / 5) * 5);
    }
    return 0; // fresh schedules default to :00
  });
  const [platforms, setPlatforms] = useState<string[]>(() => {
    if (originalSnapshot) return [...originalSnapshot.platforms];
    // Pre-select only a CONNECTED platform — never a disconnected one. Hard-
    // coding ['instagram'] opened the modal with IG checked-but-blocked (the
    // "not connected" warning fired immediately and tapping it just removed
    // it). Prefer IG, then FB; if neither is connected, start with nothing
    // selected so both render as disabled "CONNECT" chips (the correct
    // default). Check the real connection fields directly — NOT
    // platformAvailability, which treats a still-loading null status as
    // "connected" and would wrongly pre-select.
    if (fbStatus?.instagramUserId) return ['instagram'];
    if (fbStatus?.connected) return ['facebook'];
    return [];
  });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ----- Lifecycle -----
  // Lock body scroll + Escape to close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  // ----- Derived -----
  const selectedPost = useMemo(
    () => posts.find((p) => p.id === selectedPostId) ?? null,
    [posts, selectedPostId],
  );

  // Build the scheduled_at ISO timestamp (UTC) from date + hour + minute.
  const scheduledAtISO = useMemo(() => {
    const [y, m, d] = dateStr.split('-').map((s) => parseInt(s, 10));
    if (!y || !m || !d) return null;
    const local = new Date(y, m - 1, d, hour, minute, 0, 0);
    return local.toISOString();
  }, [dateStr, hour, minute]);

  const isInFuture = useMemo(() => {
    if (!scheduledAtISO) return false;
    return new Date(scheduledAtISO) > new Date();
  }, [scheduledAtISO]);

  // Edit mode: did the user change anything vs the original cohort?
  // - timeChanged: hour or day differs from the original scheduled_at
  // - platformsChanged: the set of selected platforms differs
  // Disables Save Changes until at least one of these is true.
  const hasChanges = useMemo(() => {
    if (!originalSnapshot) return true; // create mode is always "changed"
    const timeChanged = scheduledAtISO !== null
      && new Date(scheduledAtISO).getTime() !== new Date(originalSnapshot.scheduledAt).getTime();
    const currentSorted = [...platforms].sort();
    const platformsChanged =
      currentSorted.length !== originalSnapshot.platforms.length ||
      currentSorted.some((p, i) => p !== originalSnapshot.platforms[i]);
    return timeChanged || platformsChanged;
  }, [originalSnapshot, scheduledAtISO, platforms]);

  // Selected platforms that aren't actually connected — e.g. the default
  // Instagram pick when no IG Business account is linked to the Facebook
  // Page. Blocks submit AND surfaces an explicit "not connected" hint
  // instead of falling through to the time-in-past message. Empty while
  // fbStatus is still loading (platformAvailability treats null as
  // connected), so there's no false "not connected" flash on open.
  const unconnectedSelected = useMemo(
    () =>
      platforms
        .filter((id) => platformAvailability(id, fbStatus).state !== 'connected')
        .map((id) => PLATFORM_META[id]?.label ?? id),
    [platforms, fbStatus],
  );

  const canSubmit =
    !saving && !!selectedPostId && platforms.length > 0
    && unconnectedSelected.length === 0 && isInFuture && hasChanges;

  // ----- Handlers -----
  const togglePlatform = (id: string) => {
    // Block adding an unconnected platform — but always allow REMOVING
    // (so users can clean up tiles scheduled before a platform's OAuth
    // got revoked). Connection-aware UX consistent with the "Post Now"
    // hover icons on gallery tiles.
    const availability = platformAvailability(id, fbStatus);
    const isCurrentlyOn = platforms.includes(id);
    if (!isCurrentlyOn && availability.state !== 'connected') return;
    setPlatforms((prev) =>
      prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id],
    );
  };

  const handleSubmit = useCallback(async () => {
    if (!scheduledAtISO || !selectedPostId) return;
    setError(null);
    setSaving(true);
    try {
      if (isEdit && originalSnapshot) {
        // Multi-platform edit: diff the cohort against current selection.
        // - Removed platforms → DELETE their tile.
        // - Kept platforms    → UPDATE their tile to the new scheduled_at.
        // - Added platforms   → INSERT new tiles via the create endpoint
        //                       (single round-trip, fans out internally).
        const currentSet = new Set(platforms);
        const originalSet = new Set(originalSnapshot.platforms);
        const removed = originalSnapshot.platforms.filter((p) => !currentSet.has(p));
        const kept = originalSnapshot.platforms.filter((p) => currentSet.has(p));
        const added = platforms.filter((p) => !originalSet.has(p));

        // DELETE removed tiles in parallel.
        await Promise.all(
          removed.map((p) => {
            const tile = originalSnapshot.tilesByPlatform.get(p);
            return tile ? deleteScheduledPost(tile.id) : Promise.resolve();
          }),
        );

        // UPDATE kept tiles to the new time (skip the API call when time
        // didn't change — pure platform-set edit shouldn't churn DB rows).
        const timeChanged =
          new Date(scheduledAtISO).getTime() !==
          new Date(originalSnapshot.scheduledAt).getTime();
        if (timeChanged) {
          await Promise.all(
            kept.map((p) => {
              const tile = originalSnapshot.tilesByPlatform.get(p);
              return tile
                ? updateScheduledPost(tile.id, { scheduledAt: scheduledAtISO, platform: p })
                : Promise.resolve();
            }),
          );
        }

        // INSERT new platforms in one batched call.
        if (added.length > 0) {
          await createScheduledPosts({
            postId: selectedPostId,
            scheduledAt: scheduledAtISO,
            platforms: added,
          });
        }
      } else {
        await createScheduledPosts({
          postId: selectedPostId,
          scheduledAt: scheduledAtISO,
          platforms,
        });
      }
      onSaved();
    } catch (err) {
      const msg = err instanceof InsufficientCreditsError
        ? err.message
        : err instanceof Error
          ? err.message
          : 'Something went wrong. Try again.';
      setError(msg);
    } finally {
      setSaving(false);
    }
  }, [scheduledAtISO, selectedPostId, platforms, isEdit, originalSnapshot, onSaved]);

  const handleDelete = useCallback(async () => {
    if (!editingTiles || editingTiles.length === 0) return;
    const count = editingTiles.length;
    const ok = await confirm({
      title: count > 1 ? `Cancel ${count} scheduled posts?` : 'Cancel this scheduled post?',
      message:
        count > 1
          ? 'This will cancel every platform you scheduled at this time. The source post stays in My Creatives.'
          : 'The source post stays in My Creatives.',
      confirmLabel: count > 1 ? 'Cancel all' : 'Cancel post',
      cancelLabel: 'Keep them',
      destructive: true,
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await Promise.all(editingTiles.map((t) => deleteScheduledPost(t.id)));
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not delete.');
    } finally {
      setDeleting(false);
    }
  }, [editingTiles, onSaved, confirm]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={isEdit ? 'Edit scheduled post' : 'Schedule a post'}
    >
      <div
        className="relative w-full max-w-2xl max-h-[88vh] overflow-y-auto rounded-2xl glass-stroke bg-white shadow-2xl shadow-primary/15"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-white/90 border border-surface-dark/30 text-dark-muted hover:text-dark hover:bg-white shadow-sm flex items-center justify-center cursor-pointer transition-colors"
          aria-label="Close"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-surface-dark/20">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
              <i className={`text-lg ${isEdit ? 'ri-edit-line' : 'ri-calendar-event-line'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display font-bold text-dark text-base">
                {isEdit ? 'Edit scheduled post' : 'Schedule a post'}
              </h3>
              <p className="text-xs text-dark-muted mt-0.5">
                {isEdit
                  ? editingTiles && editingTiles.length > 1
                    ? `Editing ${editingTiles.length} platforms scheduled at the same time. Toggle to add/remove, change the time, or cancel.`
                    : 'Move it to a different time, add platforms, or cancel.'
                  : 'Pick an approved post, when it should go out, and which platforms.'}
              </p>
            </div>
          </div>
          {/* Ad Pilot deep-link — when the cohort was driven by an
              Ad Strategist run (Ad Pilot weekly spawn), surface the
              "open the AS row" affordance so the user can see the brief +
              concept that produced this scheduled ad. */}
          {isEdit && editingTiles && (() => {
            const asJobId = editingTiles.find((t) => t.source_job_id)?.source_job_id;
            if (!asJobId) return null;
            return (
              <a
                href={`/ad-strategist?job=${encodeURIComponent(asJobId)}`}
                target="_blank"
                rel="noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/10 text-accent text-xs font-bold hover:bg-accent/15 transition-colors cursor-pointer"
              >
                <i className="ri-flight-takeoff-line" />
                View Ad Strategist run
                <i className="ri-external-link-line text-[10px]" />
              </a>
            );
          })()}
        </div>

        <div className="p-6 space-y-5">
          {/* Post picker (create mode) or locked post preview (edit mode) */}
          {isEdit ? (
            <LockedPostPreview post={selectedPost} tile={editingTiles?.[0] ?? null} />
          ) : (
            <PostPicker
              posts={posts}
              selectedId={selectedPostId}
              onSelect={setSelectedPostId}
            />
          )}

          {/* Date + time */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-dark mb-1.5">Date</label>
              <input
                type="date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                min={toLocalDateInputValue(new Date())}
                className="w-full px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-dark mb-1.5">Time (local)</label>
              <div className="flex items-center gap-1.5">
                <select
                  value={hour}
                  onChange={(e) => setHour(parseInt(e.target.value, 10))}
                  aria-label="Hour"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none bg-white"
                >
                  {HOURS_24.map((h) => (
                    <option key={h} value={h}>{format12h(h)}</option>
                  ))}
                </select>
                <span className="text-dark-muted font-bold shrink-0">:</span>
                <select
                  value={minute}
                  onChange={(e) => setMinute(parseInt(e.target.value, 10))}
                  aria-label="Minute"
                  className="flex-1 min-w-0 px-3 py-2 rounded-lg border border-dark-muted/40 text-sm focus:border-primary outline-none bg-white"
                >
                  {MINUTES.map((mm) => (
                    <option key={mm} value={mm}>{String(mm).padStart(2, '0')}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Platforms — multi-select in BOTH create and edit modes.
              Unconnected platforms render as disabled with a tooltip
              explaining how to enable them (or "Coming soon" for TikTok
              + X). Users can still UNcheck a previously-scheduled tile on
              a now-disconnected platform — keeps stale schedules
              cleanable without forcing them to the calendar page. */}
          <div>
            <label className="block text-xs font-semibold text-dark mb-1.5">
              Platforms
              <span className="text-dark-muted font-normal">
                {isEdit
                  ? ' (toggle to add or remove — one tile per platform)'
                  : ' (pick one or more — each creates a separate scheduled post)'}
              </span>
            </label>
            <div className="flex flex-wrap gap-2">
              {PLATFORM_PICK_ORDER.map((id) => {
                const m = PLATFORM_META[id];
                const active = platforms.includes(id);
                const avail = platformAvailability(id, fbStatus);
                // Locked: not connected AND user hasn't pre-selected
                // it. Pre-selected stays clickable so users can uncheck
                // a stale schedule on a disconnected platform.
                const isLocked = !active && avail.state !== 'connected';
                const tooltip = avail.state === 'connected'
                  ? (active ? `Remove ${m.label}` : `Add ${m.label}`)
                  : avail.hint;
                return (
                  <button
                    key={id}
                    type="button"
                    onClick={() => togglePlatform(id)}
                    disabled={isLocked}
                    title={tooltip}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-bold transition-all border ${
                      active
                        ? 'border-primary bg-primary/5 text-dark cursor-pointer'
                        : isLocked
                          ? 'border-surface-dark/20 bg-surface/40 text-dark-muted/50 cursor-not-allowed'
                          : 'border-surface-dark/30 bg-white text-dark-muted hover:border-primary/40 cursor-pointer'
                    }`}
                  >
                    <i
                      className={m.icon}
                      style={{ color: isLocked ? undefined : m.color }}
                    />
                    {m.label}
                    {active && <i className="ri-check-line text-primary text-xs ml-0.5" />}
                    {isLocked && avail.state === 'needs_connection' && (
                      <span className="ml-1 px-1.5 py-0 rounded text-[10px] font-semibold uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
                        Connect
                      </span>
                    )}
                    {isLocked && avail.state === 'coming_soon' && (
                      <span className="ml-1 px-1.5 py-0 rounded text-[10px] font-semibold uppercase tracking-wider bg-surface-dark/40 text-dark-muted">
                        Soon
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Inline validation hint. Connection errors take priority over
              the time-in-past hint — an unconnected platform is the more
              actionable blocker (and the default Instagram pick surfaces it
              on open when no IG account is linked). */}
          {unconnectedSelected.length > 0 ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <i className="ri-link-unlink mr-1" />
              {unconnectedSelected.join(', ')}{' '}
              {unconnectedSelected.length === 1 ? 'is' : 'are'} not connected.
              Connect in the sidebar, or pick another platform.
            </p>
          ) : !isInFuture && scheduledAtISO ? (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              <i className="ri-time-line mr-1" />
              That time is in the past. Pick a future date + time.
            </p>
          ) : null}

          {/* Error surface */}
          {error && (
            <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700 flex items-start gap-2">
              <i className="ri-error-warning-line mt-0.5" />
              <div className="flex-1">{error}</div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-surface-dark/20 flex flex-wrap items-center justify-between gap-2">
          <div>
            {isEdit && (
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleting}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {deleting ? (
                  <><i className="ri-loader-4-line animate-spin" /> Deleting…</>
                ) : editingTiles && editingTiles.length > 1 ? (
                  <><i className="ri-delete-bin-line" /> Cancel all</>
                ) : (
                  <><i className="ri-delete-bin-line" /> Cancel post</>
                )}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-full bg-white border border-surface-dark/40 hover:border-primary hover:text-primary text-dark-muted text-sm font-bold transition-all cursor-pointer"
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!canSubmit}
              className="inline-flex items-center gap-1.5 px-5 py-2 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold shadow-md shadow-primary/25 transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <><i className="ri-loader-4-line animate-spin" /> Saving…</>
              ) : isEdit ? (
                <><i className="ri-save-line" /> Save changes</>
              ) : (
                <>
                  <i className="ri-calendar-check-line" />
                  Schedule {platforms.length > 1 ? `(${platforms.length} posts)` : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// PostPicker — grid of approved posts the user can pick from. Resolves
// signed URLs lazily (private gallery bucket). Selected post gets a
// primary border + check badge.
function PostPicker({
  posts,
  selectedId,
  onSelect,
}: {
  posts: Post[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  const [signedUrls, setSignedUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Picker thumbs are ~80px square — request 200px (retina-safe) WebP
      // at quality 75. Single batched round-trip via createSignedUrls.
      // Cap at 20 to keep the picker performant — pagination is a v2
      // concern; most users won't have >20 approved posts yet.
      const slice = posts.slice(0, 20).filter((p) => !p.image_path.endsWith('.zip'));
      const paths = slice.map((p) => p.image_path);
      try {
        const urlByPath = await getSignedImageUrls(paths, 3600, {
          width: 200,
          quality: 75,
        });
        if (cancelled) return;
        const byId: Record<string, string> = {};
        for (const p of slice) {
          if (urlByPath[p.image_path]) byId[p.id] = urlByPath[p.image_path];
        }
        setSignedUrls(byId);
      } catch { /* fall back to placeholders */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [posts.map((p) => p.id).join('|')]);

  if (posts.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-surface-dark/40 bg-surface/40 px-5 py-6 text-center">
        <i className="ri-image-line text-2xl text-dark-muted/60" />
        <p className="text-sm font-semibold text-dark mt-2">No approved posts yet</p>
        <p className="text-xs text-dark-muted mt-0.5">
          Save a post from the wizard or Ad Strategist to schedule one.
        </p>
      </div>
    );
  }

  return (
    <div>
      <label className="block text-xs font-semibold text-dark mb-1.5">
        Pick a post <span className="text-dark-muted font-normal">({posts.length} approved)</span>
      </label>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-[260px] overflow-y-auto pr-1">
        {posts.slice(0, 20).map((p) => {
          const isCarousel = p.image_path.endsWith('.zip');
          const url = signedUrls[p.id];
          const active = selectedId === p.id;
          return (
            <button
              key={p.id}
              type="button"
              onClick={() => onSelect(p.id)}
              className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                active
                  ? 'border-primary shadow-md shadow-primary/20'
                  : 'border-surface-dark/30 hover:border-primary/40'
              }`}
              title={p.title || p.headline || 'Post'}
            >
              {isCarousel ? (
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5 flex items-center justify-center text-dark-muted">
                  <i className="ri-stack-line text-xl" />
                </div>
              ) : url ? (
                <img
                  src={url}
                  alt={p.title || 'post'}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <div className="absolute inset-0 bg-surface-dark/30 animate-pulse" />
              )}
              {active && (
                <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary text-white flex items-center justify-center shadow-sm">
                  <i className="ri-check-line text-xs" />
                </div>
              )}
            </button>
          );
        })}
      </div>
      {posts.length > 20 && (
        <p className="text-[10px] text-dark-muted mt-1">
          Showing 20 most recent. Older posts available from My Creatives.
        </p>
      )}
    </div>
  );
}

// LockedPostPreview — shown in edit mode where the post can't be swapped.
// Shows a small chip with thumbnail + title so the user knows what they're
// editing without scrolling.
//
// Three "post not resolvable" states differentiated via the `tile`:
//   1. Ad Pilot auto-generate tile, not yet rendered → friendly "AI is
//      drafting this" message (NOT an error — expected mid-flight state).
//   2. Ad Pilot tile that DID render but the post was deleted from My
//      Creatives → red "source missing" (user broke the link).
//   3. Manual ready_post tile whose source post was deleted → same red.
function LockedPostPreview({ post, tile }: { post: Post | null; tile: ScheduledPost | null }) {
  const [signed, setSigned] = useState<string | null>(null);
  useEffect(() => {
    if (!post) return;
    if (post.image_path.endsWith('.zip')) return;
    (async () => {
      try {
        // 56px thumb — request 160px (retina) WebP at q75.
        const url = await getSignedImageUrl(post.image_path, 3600, {
          width: 160,
          quality: 75,
        });
        setSigned(url);
      } catch { /* placeholder */ }
    })();
  }, [post]);

  if (!post) {
    // Auto-generate tile that hasn't rendered yet — not an error.
    if (tile && tile.mode === 'auto_generate' && !tile.generated_post_id) {
      return (
        <div className="rounded-xl bg-accent-light/60 border border-accent/20 px-3 py-2 text-xs text-dark flex items-center gap-2">
          <i className="ri-sparkling-2-line text-accent" />
          <span>
            <strong>Ad Pilot is still drafting this ad.</strong> The image
            will appear here once it's rendered (usually within 5 minutes
            of scheduling, or by the morning before the post date).
          </span>
        </div>
      );
    }
    return (
      <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
        Source post is missing (deleted from My Creatives). You can still
        change the time or cancel this tile.
      </div>
    );
  }
  const isCarousel = post.image_path.endsWith('.zip');
  return (
    <div className="flex items-center gap-3 rounded-xl border border-surface-dark/30 bg-surface/40 p-3">
      <div className="relative w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-surface-dark/30">
        {isCarousel ? (
          <div className="absolute inset-0 flex items-center justify-center text-dark-muted bg-gradient-to-br from-primary/5 to-accent/5">
            <i className="ri-stack-line text-lg" />
          </div>
        ) : signed ? (
          <img src={signed} alt="post" className="absolute inset-0 w-full h-full object-cover" />
        ) : (
          <div className="absolute inset-0 animate-pulse bg-surface-dark/30" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold text-dark-muted uppercase tracking-wide">Post</p>
        <p className="text-sm font-bold text-dark truncate">
          {post.headline || post.title || 'Saved post'}
        </p>
        {post.tagline && (
          <p className="text-xs text-dark-muted truncate">{post.tagline}</p>
        )}
      </div>
    </div>
  );
}

// ----- utils -----

function toLocalDateInputValue(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Hour-only 12-hour label for the time picker's hour select ("2 PM"). The
// minute is chosen in the sibling select, so no ":00" here.
function format12h(h: number): string {
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display} ${period}`;
}
